import React, { useRef, useEffect } from 'react';
import { useEditor } from '../../contexts/EditorContext';
import { Group } from '../../types/group.types';
import { Tab } from '../../types/tab.types';
import { SelectedElementType } from '../../pages/Editor/ModelEditor';
import { useUIModelApi } from '../../contexts/UIModelApiContext';
import { generateComponentId } from '../../utils/IdGenerator';
import { EyeIconDisable, HideEyeIcon } from 'assests/GmsIcons';

type TabNavigationProps = {
  id?: string;
  onNavigationSelect?: () => void;
  onGroupSelect?: (groupId: string) => void;
  onPageSelect?: (pageId: string, groupId: string) => void;
  selectedElement?: SelectedElementType;
  navigationConfig?: {
    orientation?: 'Horizontal' | 'Vertical';
    style?: 'Block' | 'Stepper';
    textAlignment?: 'Left' | 'Centre' | 'Right';
    showDivider?: boolean;
    showNavigation?: boolean;
    pageSpacing?: number;
    groupSpacing?: number;
  };
}

export const TabNavigation = ({ id, onNavigationSelect, onGroupSelect, onPageSelect, selectedElement, navigationConfig}: TabNavigationProps) => {
  const baseId = id || 'tab-navigation';
  const { groups, setGroups, editorState, setEditorState } = useEditor();

  const navContainerRef = useRef<HTMLDivElement>(null);
  const { createDefaultCanvas, setCanvasMap } = useUIModelApi();
  

  const config = {
    orientation: navigationConfig?.orientation || 'Horizontal',
    style: navigationConfig?.style || 'Block',
    textAlignment: navigationConfig?.textAlignment || 'Centre',
    showDivider: navigationConfig?.showDivider !== undefined ? navigationConfig.showDivider : true,
    pageSpacing: navigationConfig?.pageSpacing !== undefined ? navigationConfig.pageSpacing : 10,
    groupSpacing: navigationConfig?.groupSpacing !== undefined ? navigationConfig.groupSpacing : 10
  };

  const { activeGroupId, activePageId } = editorState;
  
  const isNavigationSelected = selectedElement?.type === 'navigation';
  
  const isGroupSelected = (groupId: string) => 
    selectedElement?.type === 'group' && selectedElement.id === groupId;
  
  const isPageSelected = (pageId: string, groupId: string) => 
    selectedElement?.type === 'page' && 
    selectedElement.id === pageId && 
    selectedElement.parentId === groupId;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!onNavigationSelect) return;
      
      const target = e.target as HTMLElement;
      
      if (navContainerRef.current && navContainerRef.current.contains(target)) {
        if (target.tagName === 'BUTTON' || 
            target.closest('[data-ignore-click="true"]') ||
            target.closest('button') ||
            target.getAttribute('role') === 'button') {
          return;
        }
        e.stopPropagation();
        onNavigationSelect();
      }
    };

    const navContainer = navContainerRef.current;
    
    if (navContainer) {
      navContainer.addEventListener('click', handleClick as EventListener);
    }
    
    return () => {
      if (navContainer) {
        navContainer.removeEventListener('click', handleClick as EventListener);
      }
    };
  }, [onNavigationSelect, navContainerRef]);

  const handleAddGroup = async () => {
    const newPageId = generateComponentId('page');
    const newCanvasId = generateComponentId('canvas');
    const newGroupId = generateComponentId('group');

    const newGroup: Group = {
        id: newGroupId,
        name: `Group ${groups.length + 1}`,
        order: groups.length,
        orientation: 'horizontal',
        configuration: {
            showTitle: true,
        },
        pages: [
            {
                id: newPageId,
                name: `Page 1`,
                order: 0,
                configuration: {
                    autoAdvance: false,
                },
                canvas: { containers: [] }
            }
        ]
    };

    setGroups([...groups, newGroup]);

    setCanvasMap(prev => ({
        ...prev,
        [newPageId]: newCanvasId
    }));

    await createDefaultCanvas(newCanvasId, newPageId, newGroupId);

    setEditorState(prev => ({
        ...prev,
        activeGroupId: newGroup.id,
        activePageId: newGroup.pages[0].id
    }));


    if (onNavigationSelect) {
        onNavigationSelect();
    }
};

  const handleAddTab = async (groupId: string) => {
    const newPageId = generateComponentId('page');
    const newCanvasId = generateComponentId('canvas');

    setGroups(groups.map(group => {
        if (group.id === groupId) {
            const newTab: Tab = {
                id: newPageId,
                name: `Page ${group.pages.length + 1}`,
                order: group.pages.length,
                configuration: {
                    autoAdvance: false,
                },
                canvas: { containers: [] }
            };

            return {
                ...group,
                pages: [...group.pages, newTab]
            };
        }
        return group;
    }));

    setCanvasMap(prev => ({
        ...prev,
        [newPageId]: newCanvasId
    }));

    await createDefaultCanvas(newCanvasId, newPageId, groupId);

};

  const handleSelectActiveGroup = (groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    if (group && group.pages.length > 0) {
      setEditorState(prev => ({
        ...prev,
        activeGroupId: groupId,
        activePageId: group.pages[0].id,
        selectedContainerId: null 
      }));
    }
  };

  const handleSelectActiveTab = (groupId: string, pageId: string) => {
    setEditorState(prev => ({
      ...prev,
      activeGroupId: groupId,
      activePageId: pageId,
      selectedContainerId: null,
      selectionPath: [], 
    }));
  };

  const getPageSpacingStyle = () => ({
    marginRight: `${config.pageSpacing}px`
  });

  const getGroupSpacingStyle = () => ({
    marginRight: `${config.groupSpacing}px`
  });

  const getTextAlignmentStyle = () => {
    switch (config.textAlignment) {
      case 'Left':
        return 'tw-text-left';
      case 'Centre':
        return 'tw-text-center';
      case 'Right':
        return 'tw-text-right';
      default:
        return 'tw-text-center';
    }
  };

  const showDividers = config.showDivider;

  return (
    <div className={`tw-relative ${navigationConfig?.showNavigation === false ? 'tw-min-h-[80px]' : ''}`}>
      <div
        id={baseId}
        data-testid={baseId}
        ref={navContainerRef}
        className={`tw-bg-[#F4F5F7]  tw-w-full tw-relative
          ${isNavigationSelected ? 'tw-ring-2 tw-ring-blue-500 ' : ''}`}
        style={{
          overflow: 'hidden'
        }}
      >
      {navigationConfig?.showNavigation === false && (
        <div className="tw-absolute tw-inset-0 tw-bg-[#F4F5F7] tw-flex tw-items-center tw-justify-center tw-z-50 tw-rounded-md">
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-[#6D788C]">
            <HideEyeIcon />
            <span className="tw-font-semibold tw-text-base" style={{ fontFamily: "'Noto Sans', sans-serif" }}>Navigation</span>
          </div>
        </div>
      )}
      <div
        id={`${baseId}-scroll-container`}
        className="tw-overflow-x-auto tw-overflow-y-hidden"
        style={{
          width: '100%',
          scrollbarWidth: 'thin'
        }}
      >
        <div
          id={`${baseId}-groups-container`}
          className={`tw-flex tw-items-stretch tw-flex-nowrap ${config.orientation === 'Vertical' ? 'tw-flex-col' : ''}`}
        >
          {/* Render each group as a separate section */}
          {groups.map((group, index) => (
            <React.Fragment key={group.id}>
              {index > 0 && showDividers && (
                <div
                  id={`${baseId}-divider-${index}`}
                  className="tw-border-l tw-border-[#DADBDD] tw-self-stretch tw-mx-1.5 tw-flex-shrink-0"
                ></div>
              )}
              <div
                id={`${baseId}-group-${group.id}`}
                data-testid={`${baseId}-group-${group.id}`}
                className={`tw-flex tw-flex-col tw-py-2 tw-ps-3 tw-pe-3 tw-flex-shrink-0`}
                style={index < groups.length - 1 ? getGroupSpacingStyle() : undefined}
              >
                {/* Group title */}
                <div id={`${baseId}-group-${group.id}-title-wrapper`} className="tw-mb-2 ">
                  <button
                    id={`${baseId}-group-${group.id}-btn`}
                    data-testid={`${baseId}-group-${group.id}-btn`}
                    data-ignore-click="true"
                    className={`tw-px-2.5 tw-flex tw-gap-2 tw-py-0.5 tw-text-xs tw-font-medium tw-transition-colors tw-duration-200 tw-ease-in-out tw-rounded
                              ${isGroupSelected(group.id) ? 'tw-ring-2 tw-ring-blue-500' : ''}
                              ${group.id === activeGroupId ? 'tw-bg-[#E6F0FF] tw-text-[#0747A6] tw-border tw-border-[#1F7AE4]' : 'tw-text-[#6D788C] hover:tw-bg-[#EAECF0]'}
                              ${getTextAlignmentStyle()}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onGroupSelect) {
                        onGroupSelect(group.id);
                      }
                      handleSelectActiveGroup(group.id);
                    }}
                  >

                    {!group.configuration.showTitle && <EyeIconDisable />}

                    {!group.configuration.showTitle && 'Title'}

                    {group.configuration.showTitle && group.name}
                  </button>
                </div>

                {/* Group pages */}
                <div
                  id={`${baseId}-group-${group.id}-pages`}
                  className="tw-flex tw-items-center tw-gap-1.5"
                >
                  {group.pages.map((tab, tabIndex) => (
                    <button
                      key={tab.id}
                      id={`${baseId}-page-${tab.id}`}
                      data-testid={`${baseId}-page-${tab.id}`}
                      data-ignore-click="true"
                      className={`tw-px-3.5 tw-py-1.5 tw-text-xs tw-border tw-rounded tw-flex-shrink-0
                                ${isPageSelected(tab.id, group.id) ? 'tw-ring-2 tw-ring-blue-500' : ''}
                                ${tab.id === activePageId && group.id === activeGroupId
                                  ? 'tw-bg-[#BCD4F6] tw-text-[#0747A6] tw-border-[#1F7AE4] tw-shadow-sm tw-relative tw-z-10 tw-font-medium'
                                  : 'tw-bg-white tw-text-[#6D788C] tw-border-[#DADBDD] hover:tw-bg-[#F5F9FF] hover:tw-text-[#0747A6]'}
                                ${getTextAlignmentStyle()}`}
                      style={tabIndex < group.pages.length - 1 ? getPageSpacingStyle() : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onPageSelect) {
                          onPageSelect(tab.id, group.id);
                        }
                        handleSelectActiveTab(group.id, tab.id);
                      }}
                    >
                      {tab.name}
                    </button>
                  ))}

                  {/* Add Tab button for this group */}
                  <button
                    id={`${baseId}-group-${group.id}-add-page-btn`}
                    data-testid={`${baseId}-group-${group.id}-add-page-btn`}
                    data-ignore-click="true"
                    className="tw-flex tw-items-center tw-justify-center tw-h-7 tw-w-7 tw-rounded tw-border tw-border-[#DADBDD] tw-bg-white tw-text-[#6D788C] tw-flex-shrink-0 hover:tw-bg-[#F5F9FF] hover:tw-text-[#0747A6]"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddTab(group.id);
                    }}
                    aria-label="Add Page"
                    title="Add Page"
                  >
                    <span className="tw-text-xs">+</span>
                  </button>
                </div>
              </div>
            </React.Fragment>
          ))}
          
          {/* Vertical divider before "+ Group" section */}
          {showDividers && groups.length > 0 && (
            <div
              id={`${baseId}-add-group-divider`}
              className="tw-border-l tw-border-[#DADBDD] tw-self-stretch tw-mx-1.5 tw-flex-shrink-0"
            ></div>
          )}

          {/* "+ Group" section */}
          <div
            id={`${baseId}-add-group-section`}
            className="tw-flex tw-flex-col tw-py-2 tw-ps-2 tw-pe-4 tw-space-y-3 tw-self-end tw-flex-shrink-0"
          >
            <button
              id={`${baseId}-add-group-btn`}
              data-testid={`${baseId}-add-group-btn`}
              data-ignore-click="true"
              className="tw-flex tw-items-center tw-justify-center tw-font-medium tw-text-xs tw-h-6 tw-px-2.5 tw-rounded tw-border tw-border-[#0747A6] tw-bg-white tw-text-[#0747A6] hover:tw-bg-[#F5F9FF]"
              onClick={(e) => {
                e.stopPropagation();
                handleAddGroup();
              }}
            >
              <span className="tw-mr-1 tw-text-xs">+</span>
              <span className="tw-text-xs">Group</span>
            </button>

            <div
              id={`${baseId}-placeholder`}
              className="tw-border tw-bg-[#F4F5F7] tw-border-dashed tw-border-[#97A0AF] tw-h-7 tw-w-18 tw-rounded"
            ></div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}

